import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  FormField,
  QueryStateBoundary,
} from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { useInquiry } from "@/api/hooks/useInquiries";
import { usePackages } from "@/api/hooks/usePackages";
import { useCreateQuote, usePatchQuote } from "@/api/hooks/useQuotes";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { DEFAULT_PACKAGE_CODE, type Package } from "@/api/types/package";
import type { LineItemDraft, Quote } from "@/api/types/quote";
import { emptyLineItemDraft } from "@/api/types/quote";
import { PackagePickerCards } from "./PackagePickerCards";
import { LineItemsTable } from "./LineItemsTable";
import { computeQuotePreview, draftToLineItemInput } from "./quoteMath";

let rowCounter = 0;
function nextRowKey(): string {
  rowCounter += 1;
  return `row-${rowCounter}`;
}

function seedDraftFromPackage(pkg: Package): LineItemDraft[] {
  return [
    {
      key: nextRowKey(),
      label: pkg.name,
      qty: "1",
      unitDollars: (pkg.base_price_cents / 100).toFixed(2),
    },
  ];
}

function draftsFromQuote(quote: Quote): LineItemDraft[] {
  if (quote.line_items.length === 0) return [emptyLineItemDraft(nextRowKey())];
  return quote.line_items.map((li) => ({
    key: nextRowKey(),
    label: li.label,
    qty: String(li.qty),
    unitDollars: (li.unit_cents / 100).toFixed(2),
  }));
}

interface QuoteBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  /** Create mode: builds a new quote against this inquiry. */
  inquiryId?: string;
  /** Edit mode: PATCHes this existing draft quote instead of creating one. */
  existingQuote?: Quote;
  onSaved: (quote: Quote) => void;
}

/**
 * Quote builder — package picker (create mode only; a quote's package is
 * fixed after creation per the API contract) + editable line-item table +
 * service-charge/deposit inputs, with a clearly-labeled client-side preview.
 * The server recomputes every cent on save; onSaved receives the server's
 * response, which is what the caller must display.
 */
export function QuoteBuilderDialog({
  open,
  onClose,
  inquiryId,
  existingQuote,
  onSaved,
}: QuoteBuilderDialogProps) {
  const isEdit = !!existingQuote;

  const { data: inquiry, isLoading: inquiryLoading } = useInquiry(
    isEdit ? undefined : inquiryId,
  );
  const { data: packages, isLoading: packagesLoading, error: packagesError, refetch } =
    usePackages();

  const createQuote = useCreateQuote();
  const patchQuote = usePatchQuote();
  const isSaving = createQuote.isPending || patchQuote.isPending;

  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    existingQuote?.package_id ?? null,
  );
  const [items, setItems] = useState<LineItemDraft[]>(
    existingQuote ? draftsFromQuote(existingQuote) : [],
  );
  const [serviceChargePct, setServiceChargePct] = useState(
    existingQuote ? String(existingQuote.service_charge_pct) : "18",
  );
  const [depositPct, setDepositPct] = useState(
    existingQuote ? String(existingQuote.deposit_pct) : "25",
  );

  // Reset local state whenever the dialog is (re-)opened, and pick the
  // playbook default (Signature) once packages have loaded for create mode.
  useEffect(() => {
    if (!open) return;
    if (existingQuote) {
      setSelectedPackageId(existingQuote.package_id);
      setItems(draftsFromQuote(existingQuote));
      setServiceChargePct(String(existingQuote.service_charge_pct));
      setDepositPct(String(existingQuote.deposit_pct));
      return;
    }
    setSelectedPackageId(null);
    setItems([]);
    setServiceChargePct("18");
    setDepositPct("25");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingQuote?.id]);

  useEffect(() => {
    if (existingQuote || !open || !packages || packages.length === 0) return;
    if (selectedPackageId) return;
    const preferred =
      packages.find((p) => p.code === DEFAULT_PACKAGE_CODE) ?? packages[0];
    setSelectedPackageId(preferred.id);
    setItems(seedDraftFromPackage(preferred));
  }, [open, packages, existingQuote, selectedPackageId]);

  function handleSelectPackage(pkg: Package) {
    setSelectedPackageId(pkg.id);
    setItems(seedDraftFromPackage(pkg));
  }

  function handleAddRow() {
    setItems((prev) => [...prev, emptyLineItemDraft(nextRowKey())]);
  }

  const lineItemInputs = useMemo(() => items.map(draftToLineItemInput), [items]);
  const preview = useMemo(
    () =>
      computeQuotePreview(
        lineItemInputs,
        Number(serviceChargePct) || 0,
        Number(depositPct) || 0,
      ),
    [lineItemInputs, serviceChargePct, depositPct],
  );

  const canSubmit =
    (isEdit || !!selectedPackageId) &&
    items.some((i) => i.label.trim() && Number(i.qty) > 0);

  async function handleSubmit() {
    const payloadItems = lineItemInputs.filter(
      (li) => li.label.trim() && li.qty > 0,
    );

    try {
      if (isEdit && existingQuote) {
        const quote = await patchQuote.mutateAsync({
          id: existingQuote.id,
          line_items: payloadItems,
          service_charge_pct: Number(serviceChargePct),
          deposit_pct: Number(depositPct),
        });
        toastSuccess("Quote updated");
        onSaved(quote);
      } else if (inquiryId && selectedPackageId) {
        const quote = await createQuote.mutateAsync({
          inquiry_id: inquiryId,
          package_id: selectedPackageId,
          line_items: payloadItems,
          service_charge_pct: Number(serviceChargePct),
          deposit_pct: Number(depositPct),
        });
        toastSuccess("Quote created");
        onSaved(quote);
      }
    } catch (err) {
      toastError("Couldn't save quote", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel={isEdit ? "Edit quote" : "New quote"}>
      <DialogContent size="2xl">
        <DialogHeader onClose={onClose}>{isEdit ? "Edit quote" : "New quote"}</DialogHeader>
        <DialogBody className="space-y-6">
          {!isEdit && inquiryId && (
            <QueryStateBoundary isLoading={inquiryLoading}>
              {inquiry && (
                <p className="text-sm text-text-secondary">
                  Quoting <span className="font-medium text-text-primary">{inquiry.contact_name}</span>
                  {inquiry.event_date ? ` — ${inquiry.event_date}` : ""}
                  {inquiry.guest_count != null ? ` — ${inquiry.guest_count} guests` : ""}
                </p>
              )}
            </QueryStateBoundary>
          )}

          {!isEdit && (
            <QueryStateBoundary
              isLoading={packagesLoading}
              error={packagesError}
              onRetry={() => void refetch()}
            >
              <PackagePickerCards
                packages={packages ?? []}
                selectedId={selectedPackageId}
                onSelect={handleSelectPackage}
                disabled={isSaving}
              />
            </QueryStateBoundary>
          )}

          {isEdit && (
            <p className="text-sm text-text-secondary">
              Package is fixed after a quote is created — edit line items and rates below.
            </p>
          )}

          <LineItemsTable
            items={items}
            onChange={setItems}
            onAdd={handleAddRow}
            disabled={isSaving}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Service charge %"
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={serviceChargePct}
              disabled={isSaving}
              onChange={(e) => setServiceChargePct(e.target.value)}
              hint="In the contract as a service charge — never called a tip."
            />
            <FormField
              label="Deposit %"
              type="number"
              min={0}
              max={100}
              step="0.1"
              value={depositPct}
              disabled={isSaving}
              onChange={(e) => setDepositPct(e.target.value)}
            />
          </div>

          <div className="rounded-md border border-border bg-bg-muted/50 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
              Estimated — server confirms on save
            </p>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between">
                <dt className="text-text-secondary">Subtotal</dt>
                <dd className="font-medium text-text-primary" data-testid="preview-subtotal">
                  {centsToUsd(preview.subtotal_cents)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-text-secondary">Service charge ({serviceChargePct || 0}%)</dt>
                <dd className="font-medium text-text-primary">
                  {centsToUsd(preview.service_charge_cents)}
                </dd>
              </div>
              <div className="flex justify-between border-t border-border pt-1">
                <dt className="font-semibold text-text-primary">Total</dt>
                <dd className="font-semibold text-text-primary" data-testid="preview-total">
                  {centsToUsd(preview.total_cents)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-text-secondary">Deposit due ({depositPct || 0}%)</dt>
                <dd className="font-medium text-text-primary" data-testid="preview-deposit">
                  {centsToUsd(preview.deposit_cents)}
                </dd>
              </div>
            </dl>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={!canSubmit || isSaving}>
            {isSaving ? "Saving..." : isEdit ? "Save changes" : "Create quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
